import { useSetAtom } from 'jotai';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Breadcrumb, Container } from '@/lib/v2/components';

import { useFeatureFlag } from '@/src/application/hooks/util/useFeatureFlag';
import { atomLastBill } from '@/src/modules/MyPlanModule/atoms/BillingAndConsumption';
import { useBilling } from '@/src/modules/MyPlanModule/hooks/useBilling';
import { useLegalBillingPdf } from '@/src/modules/MyPlanModule/hooks/useLegalBillingPdf';
import {
  Billing,
  Consumption,
  HansaInvoice,
  PaginationDataResponse,
} from '@/src/modules/MyPlanModule/types';

import { useConsumptionAndBillingTables } from '@/modules/MyPlanModule/hooks/useConsumptionAndBillingTables';
import {
  BillingAndConsumptionTables,
  HeaderMyPlan,
  InformationMyPlan,
} from '@/modules/MyPlanModule/screens/BillingAndConsumptionV2/components';
import BillingTable from '@/modules/MyPlanModule/screens/BillingAndConsumptionV2/components/BillingTable';
import ConsumptionTable from '@/modules/MyPlanModule/screens/BillingAndConsumptionV2/components/ConsumptionTable';
import LegalBillingTable from '@/modules/MyPlanModule/screens/BillingAndConsumptionV2/components/LegalBillingTable';

const BillingAndConsumption = () => {
  const { billingInformation, billingInformationIsLoading } = useBilling();
  const setLastBill = useSetAtom(atomLastBill);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { downloadPdf, downloadingSerialNumbers } = useLegalBillingPdf();
  const showLegalBillings = useFeatureFlag('showLegalBillings');

  const {
    list: consumptionList,
    listIsLoading: consumptionListIsLoading,
    totalPages: consumptionListTotalPages,
    changeOrderBy: handleConsumptionChangeOrderBy,
    changeTablePage: handleConsumptionChangeTablePage,
  } = useConsumptionAndBillingTables<PaginationDataResponse<Consumption[]>>('listConsumption');

  const {
    list: billingList,
    listIsLoading: billingListIsLoading,
    totalPages: billingListTotalPages,
    changeOrderBy: handleBillingChangeOrderBy,
    changeTablePage: handleBillingChangeTablePage,
  } = useConsumptionAndBillingTables<PaginationDataResponse<Billing[]>>('listBilling');

  const {
    list: legalBillingList,
    listIsLoading: legalBillingListIsLoading,
    totalPages: legalBillingListTotalPages,
    changeTablePage: handleLegalBillingChangeTablePage,
    changeOrderBy: handleLegalBillingChangeOrderBy,
    error: legalBillingListError,
  } = useConsumptionAndBillingTables<PaginationDataResponse<HansaInvoice[]>>('listLegalBilling');

  const { list: lastBillingList } =
    useConsumptionAndBillingTables<PaginationDataResponse<Billing[]>>('listBilling');

  useEffect(() => {
    if (lastBillingList) {
      setLastBill(lastBillingList.result[0]);
    }
  }, [lastBillingList, setLastBill]);

  const handleOnBackClick = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const billingAndConsumptionPaths = useMemo(
    () => [
      {
        id: 'billing-path',
        name: t('BREADCRUMB.billingAndConsumption'),
      },
    ],
    [t]
  );

  const showLegalBillingTable = useMemo(() => {
    if (!billingInformation || !showLegalBillings) {
      return false;
    }
    const ARGENTINA_COUNTRY_NUMBER = 1 as unknown as string;
    const countryNumbersAllowedToShowLegalBillingTable = [ARGENTINA_COUNTRY_NUMBER];
    return countryNumbersAllowedToShowLegalBillingTable.includes(billingInformation.country);
  }, [billingInformation, showLegalBillings]);

  return (
    <div className="min-h-[calc(100vh_-_64px)] w-full bg-emblueLightGray">
      <Breadcrumb
        withoutBackArrow
        customPaths={billingAndConsumptionPaths}
        handleOnBackClick={handleOnBackClick}
      />
      <Container noPaddingY fluid="screen">
        <HeaderMyPlan
          businessName={billingInformation?.businessName}
          contractId={billingInformation?.contractId}
          isLoading={billingInformationIsLoading}
        />
        <InformationMyPlan />
        <BillingAndConsumptionTables
          billingTable={
            <BillingTable
              billingList={billingList?.result}
              billingListIsLoading={billingListIsLoading}
              changeTableOrder={handleBillingChangeOrderBy}
              changeTablePage={handleBillingChangeTablePage}
              totalBillingList={billingList?.pager.countTotal || 0}
              totalPages={billingListTotalPages}
            />
          }
          consumptionTable={
            <ConsumptionTable
              changeTableOrder={handleConsumptionChangeOrderBy}
              changeTablePage={handleConsumptionChangeTablePage}
              consumptionList={consumptionList?.result}
              isLoadingConsumptionList={consumptionListIsLoading}
              totalConsumptionList={consumptionList?.pager.countTotal || 0}
              totalPages={consumptionListTotalPages}
            />
          }
          legalBillingTable={
            showLegalBillingTable ? (
              <LegalBillingTable
                changeTableOrder={handleLegalBillingChangeOrderBy}
                changeTablePage={handleLegalBillingChangeTablePage}
                downloadingSerialNumbers={downloadingSerialNumbers}
                error={legalBillingListError}
                isLoadingLegalBillingList={legalBillingListIsLoading}
                legalBillingList={legalBillingList?.result}
                totalLegalBillingList={legalBillingList?.pager.countTotal || 0}
                totalPages={legalBillingListTotalPages}
                onDownload={downloadPdf}
              />
            ) : undefined
          }
        />
      </Container>
    </div>
  );
};

export default memo(BillingAndConsumption);
